import React, { useState } from 'react';
import { Button, DualDateRangePicker, Modal, Select, cn } from '@crisellevaldez/speculo-ui';
import type { DateRange } from '@crisellevaldez/speculo-ui';
import { Download } from 'lucide-react';
import { convertDateRangeForDisplay } from '../../../../utils/dateRangeTimezone';
import { FilterButton } from '../../../common/FilterButton';
import { OffsetPagination } from '../../../common/OffsetPagination';
import { ChannelConsentCard } from './ChannelConsentCard';
import { ConsentHistoryTimeline } from './ConsentHistoryTimeline';
import { useFlywheelEnabled } from '../../../../stores/useOrganizationStore';
import type {
  ChannelConsentSummary,
  ConsentChannel,
  ConsentHistoryEvent,
  ConsentHistoryFilter,
} from './consent.types';

/** Applying an unchanged range would reset the trail to page one for nothing. */
const isSameRange = (a: DateRange, b: DateRange): boolean =>
  (a.from?.getTime() ?? null) === (b.from?.getTime() ?? null) &&
  (a.to?.getTime() ?? null) === (b.to?.getTime() ?? null);

const FILTER_OPTIONS: { value: ConsentHistoryFilter; label: string }[] = [
  { value: 'all', label: 'All channels' },
  { value: 'sms', label: 'SMS' },
  { value: 'phone', label: 'Calls' },
  { value: 'email', label: 'Email' },
];

/**
 * Trail filters for an org without SMS.
 *
 * "All channels" is dropped rather than kept: history filtering is server-side
 * and the resolver matches one exact channel, so there is no way to ask for
 * "everything except SMS" — an "All" option would quietly serve SMS rows back.
 * Restoring it needs the resolver to accept a channel list.
 */
const NON_SMS_FILTER_OPTIONS = FILTER_OPTIONS.filter(
  (o) => o.value !== 'sms' && o.value !== 'all'
);

export interface CommunicationConsentSectionProps {
  /** Rendered in the order given — one card per channel. */
  channels: ChannelConsentSummary[];
  /** Audit trail for the active filter, newest first. Filtering is server-side. */
  history: ConsentHistoryEvent[];
  /** Total events matching the active filters, across all pages. */
  historyTotal: number;
  /** Zero-based, as OffsetPagination expects — it renders `historyPage + 1`. */
  historyPage: number;
  historyPageSize: number;
  onHistoryPageChange: (page: number) => void;
  historyFilter: ConsentHistoryFilter;
  historyDateRange: DateRange;
  onHistoryDateRangeChange: (range: DateRange) => void;
  onHistoryFilterChange: (filter: ConsentHistoryFilter) => void;
  historyLoading?: boolean;
  onExportReport?: () => void;
  /** Omit to render the channel cards read-only. */
  onChannelToggle?: (channel: ConsentChannel, optedIn: boolean) => void;
  /** Channel with a consent write in flight — its toggle is disabled. */
  updatingChannel?: ConsentChannel | null;
  /** IANA zone for rendering timestamps. Defaults to the viewer's local zone. */
  timeZone?: string;
  className?: string;
}

export const CommunicationConsentSection: React.FC<CommunicationConsentSectionProps> = ({
  channels,
  history,
  historyTotal,
  historyPage,
  historyPageSize,
  onHistoryPageChange,
  historyFilter,
  onHistoryFilterChange,
  historyDateRange,
  onHistoryDateRangeChange,
  historyLoading = false,
  onExportReport,
  onChannelToggle,
  updatingChannel = null,
  timeZone,
  className,
}) => {
  // Read the entitlement here rather than taking it as a prop, so a new caller
  // cannot leak SMS by forgetting to pass it.
  const flywheelEnabled = useFlywheelEnabled();
  const filterOptions = flywheelEnabled ? FILTER_OPTIONS : NON_SMS_FILTER_OPTIONS;
  // Reset has to land on a filter the org is actually allowed to ask for.
  // Resetting to "all" without SMS would serve SMS rows straight back even
  // though the option is gone from the dropdown.
  const defaultFilter: ConsentHistoryFilter = flywheelEnabled ? 'all' : 'phone';
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The modal edits a draft and commits on Filter. Refetching on every filter
  // change meant picking a channel reloaded the trail before the date range was
  // chosen, so a two-part filter cost two round trips and the intermediate
  // result flashed on screen.
  //
  // The draft holds the picker's own local dates for the whole edit. The applied
  // range is anchored to the org zone, so it is converted on the way in and back
  // out again at commit — never while the draft is being edited. Converting a
  // draft that already holds local dates shifts the calendar day whenever the
  // org zone sits behind the viewer's: an LA org viewed from UTC turned a picked
  // 13 Aug into a displayed 12 Aug, and that wrong day is what got submitted.
  const [draftFilter, setDraftFilter] = useState<ConsentHistoryFilter>(historyFilter);
  const [draftDateRange, setDraftDateRange] = useState<DateRange>(() =>
    convertDateRangeForDisplay(historyDateRange, timeZone)
  );

  const hasDateFilter = Boolean(historyDateRange.from || historyDateRange.to);
  const activeFilterCount =
    (historyFilter === defaultFilter ? 0 : 1) + (hasDateFilter ? 1 : 0);

  // Seeded here rather than in an effect: the draft only needs to match the
  // applied filters at the moment the modal opens.
  const openFilters = () => {
    setDraftFilter(historyFilter);
    setDraftDateRange(convertDateRangeForDisplay(historyDateRange, timeZone));
    setFiltersOpen(true);
  };

  /** Dismissing without filtering leaves the trail on the applied filters. */
  const dismissFilters = () => setFiltersOpen(false);

  /**
   * Only what changed is sent — either handler resets the trail to page one.
   * `range` is in picker-local dates, so the applied range is brought into the
   * same representation before comparing.
   */
  const commit = (filter: ConsentHistoryFilter, range: DateRange) => {
    if (filter !== historyFilter) onHistoryFilterChange(filter);
    if (!isSameRange(range, convertDateRangeForDisplay(historyDateRange, timeZone))) {
      onHistoryDateRangeChange(range);
    }
    setFiltersOpen(false);
  };

  const handleFilter = () => commit(draftFilter, draftDateRange);

  // Applies straight away rather than staging, matching the contacts filter.
  const handleReset = () => {
    const emptyRange: DateRange = { from: null, to: null };
    setDraftFilter(defaultFilter);
    setDraftDateRange(emptyRange);
    commit(defaultFilter, emptyRange);
  };

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col gap-4', className)}>
      <div className="flex flex-col gap-3 border-foreground/15 pb-3 lg:flex-row lg:items-end lg:justify-between lg:gap-6 lg:pb-[18px]">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            Communication consent
          </h2>
          <p className="mt-0.5 text-[11px] leading-snug text-foreground/80 lg:text-[11px] xl:text-xs 2xl:text-[13px]">
            Permissions, full audit trail, and downloadable pdf for every channel Remi uses to reach
            this contact.
          </p>
        </div>
        {onExportReport && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onExportReport}
            className="w-full shrink-0 justify-center gap-2 rounded-md bg-[#FBBF24] lg:w-auto px-[15px] py-[9px] text-[13px] font-semibold tracking-[-0.005em] text-black hover:bg-[#EAAE13] focus-visible:ring-[#FBBF24]"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={2} />
            Export Report
          </Button>
        )}
      </div>

      {/* Column count tracks the cards actually rendered — without SMS there
          are two, and a hardcoded three left an empty cell on xl. Full class
          strings, since JIT purges interpolated ones. */}
      <div
        className={cn(
          'grid shrink-0 grid-cols-1 gap-4 xl:gap-5',
          channels.length === 3 ? 'xl:grid-cols-3' : 'xl:grid-cols-2'
        )}
      >
        {channels.map((summary) => (
          <ChannelConsentCard
            key={summary.channel}
            summary={summary}
            timeZone={timeZone}
            onToggle={onChannelToggle}
            isUpdating={updatingChannel === summary.channel}
          />
        ))}
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col lg:mt-6">
        <div className="flex flex-col gap-3 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-col gap-0.5">
            <h3 className="text-[15px] font-semibold tracking-[-0.015em] text-foreground">
              Consent History
            </h3>
            <span className="text-xs text-muted-foreground">
              {historyTotal > 0
                ? `${historyTotal.toLocaleString()} event${historyTotal === 1 ? '' : 's'}, newest first`
                : 'Complete audit trail, newest first'}
            </span>
          </div>

          <FilterButton
            onClick={openFilters}
            activeCount={activeFilterCount}
            className="h-9 w-full justify-center lg:w-[140px]"
            hideLabelOnMobile
          />
        </div>

        <div className="scrollbar-custom min-h-0 flex-1 overflow-y-auto pr-3">
          <ConsentHistoryTimeline events={history} timeZone={timeZone} />
        </div>

        {historyTotal > historyPageSize && (
          <OffsetPagination
            currentPage={historyPage}
            totalItems={historyTotal}
            pageSize={historyPageSize}
            onNextPage={() => onHistoryPageChange(historyPage + 1)}
            onPrevPage={() => onHistoryPageChange(Math.max(0, historyPage - 1))}
            onPageChange={onHistoryPageChange}
            isLoading={historyLoading}
            label="events"
          />
        )}
      </div>

      <Modal isOpen={filtersOpen} onClose={dismissFilters} size="base">
        <Modal.Header>Filters</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4 pb-2 sm:flex-row sm:items-start">
            <div className="flex w-full flex-col gap-1.5 sm:w-[180px] sm:shrink-0">
              <span className="text-xs font-medium text-foreground">Channel</span>
              <Select
                options={filterOptions}
                value={draftFilter}
                onChange={(value) => setDraftFilter(value as ConsentHistoryFilter)}
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground">Date range</span>
              <DualDateRangePicker
                // Already picker-local — converting here would shift the day.
                value={draftDateRange}
                onChange={setDraftDateRange}
                showPresets
                maxDate={new Date()}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="primary" onClick={handleFilter}>
              Filter
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
